import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { Element } from 'react-scroll';
import {
  find, first, values, reduce, filter,
  concat, each, defer, camelCase, findIndex,
} from 'lodash';
import numeral from 'numeral';
import Fa from 'react-fontawesome';
import { Dropdown, MenuItem, OverlayTrigger, Tooltip, ProgressBar } from 'react-bootstrap';
import ProgressBar2 from '../../../../components/progress-bar';
import { getTypeColorFromKey } from '../../../../util/helpers';
import styles from './styles.css';
import chartStyles from '../brenchmark-graph/chart-styles.css';
import MoneyDisplay from '../../../../components/money-display';
import NumberDisplay from '../../../../components/number-display';
import OwnerSelector from '../../../../components/owner-selector';
import WindowResizeListener from '../../../../components/window-size-listener';

import {
  getVelocityGraphLoading,
  getVelocityGraphLoaded,
  getVelocityGraphPipeline,
  getVelocityGraphOwners,
  getStages,
} from '../../PipelineReducer';

const typeOptions = [
  { label: 'Amount', value: 'amount' },
  { label: 'Count', value: 'count' },
];

const USER_PREFERENCE_REPORT_TYPE = 'pipeline-velocity-graph-report-type';

const minBarWidth = 40; // pixel


class VelocityGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportType: 'amount',
      ownerId: null,
      containerWidth: 0,
    };
    this.setReportType = this.setReportType.bind(this);
    this.handleChartClick = this.handleChartClick.bind(this);
    this.handleSelectOwner = this.handleSelectOwner.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    this.setState({ reportType });
  }

  onWindowResize() {
    if (this.container) {
      const containerWidth = this.container.offsetWidth;
      this.setState({ containerWidth });
    }
  }

  setReportType(reportType) {
    this.setState({ reportType });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
  }

  getStatsTooltip(stats, rowType, id, title, stage, isTotal) {
    let footer = null;
    const q1 = stage.lower_quartile_age;
    const q3 = stage.upper_quartile_age;
    if (rowType === 'accelerated') {
      footer = (
        <span>
          * Opportunities moving faster through the sales process compared to the time it normally takes you to win.
        </span>
      );
    } else if (rowType === 'lagging') {
      footer = (
        <span>
          * Opportunities moving at least 25% slower through the sales process than the average time it normally takes you to win.
        </span>
      );
    } else if (rowType === 'onTrack') {
      footer = (
        <span>
          * Opportunities moving at the expected, or normal, pace through the sales process based on the time it normally takes you to win.
        </span>
      );
    } else if (rowType === 'recent') {
      footer = (
        <span>
          * Opportunities added to the {stage.label} stage within the last <NumberDisplay value={(q1 - 1)} precision={0} suffix="day" suffixIsUnit />.
        </span>
      );
    } else if (rowType === 'stalled') {
      footer = (
        <span>
          * Opportunities sitting in the {stage.label} stage
          for more than <NumberDisplay value={q3} precision={0} suffix="day" suffixIsUnit />, which is longer than your previously won opportunities spend in this stage.
        </span>
      );
    } else if (rowType === 'working') {
      footer = (
        <span>
          * Opportunities in the sales stage that are not recent or stalled.
        </span>
      );
    }
    const dealTypes = stats ? stats.stats : {};
    const dealTypeRows = [];
    each(dealTypes, (dealType, k) => {
      dealTypeRows.push((
        <tr key={camelCase(k)}>
          <td style={{ color: getTypeColorFromKey(k) }}>
            {k}
          </td>
          <td className="text-right">
            <NumberDisplay value={dealType.count} precision={0} />
          </td>
          <td className="text-right">
            <MoneyDisplay value={dealType.amount} abbreviated precision={1} />
          </td>
        </tr>));
    });
    dealTypeRows.push((
      <tr key="total">
        <td>
          <strong>Total</strong>
        </td>
        <td className="text-right">
          <strong><NumberDisplay value={stats ? stats.count : 0} precision={0} /></strong>
        </td>
        <td className="text-right">
          <strong><MoneyDisplay value={stats ? stats.amount : 0} abbreviated precision={1} /></strong>
        </td>
      </tr>));
    return (
      <Tooltip
        id={id}
        className={chartStyles.tooltip}
      >
        <div>
          <h1 className={chartStyles.tooltipTitle}>
            {title}
          </h1>
          {!isTotal &&
            <h2 className={chartStyles.tooltipSubTitle}>
              Stage: {stage.label}
            </h2>
          }
          {dealTypeRows.length > 0 &&
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>&nbsp;</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {dealTypeRows}
              </tbody>
            </table>
          }
          <table className="table table-condensed">
            <tfoot>
              <tr>
                <td>
                  <em>{footer}</em>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Tooltip>
    );
  }
  handleChartClick(event) {
    this.props.onSelect(event.point.options.ids);
  }

  handleSelectOwner(owner) {
    this.setState({ ownerId: owner ? owner.user_id : null });
    defer(() => {
      this.onWindowResize();
    });
  }

  renderControls() {
    const { reportType } = this.state;
    const activeType = find(typeOptions, { value: reportType }) || first(typeOptions);

    return (
      <h5 className="sub-header-with-controls">
        Analyze opportunity&nbsp;
        <Dropdown
          id="type-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {activeType.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {typeOptions.map(s => (
              <MenuItem
                key={s.value}
                active={s.value === activeType.value}
                onSelect={() => { this.setReportType(s.value); }}
              >
                {s.label}
              </MenuItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;and compare with&nbsp;
        <OwnerSelector
          owners={this.props.owners}
          ownerId={this.state.ownerId}
          onSelect={this.handleSelectOwner}
        />
      </h5>
    );
  }
  renderBar(now, label, ids, rowType) {
    let bsStyle = null;
    if (rowType === 'accelerated' || rowType === 'recent') {
      bsStyle = 'success';
    } else if (rowType === 'lagging' || rowType === 'stalled') {
      bsStyle = 'danger';
    } else if (rowType === 'onTrack' || rowType === 'working') {
      bsStyle = 'warning';
    }
    return (
      <ProgressBar
        className={chartStyles.progress}
        now={now}
        label={label}
        bsStyle={bsStyle}
        onClick={() => {
          this.props.onSelect(ids);
        }}
      />
    );
  }

  renderOwnerCells(stats, otherStats, rowType, stage, rowLabel, isTotal) {
    const { reportType } = this.state;

    const stackVal = stats ? stats[camelCase(`${reportType}`)] : 0;
    const val = stats ? stats[camelCase(`${reportType} percentage`)] : 0;
    const otherValue = otherStats ? otherStats[camelCase(`${reportType} percentage`)] : 0;
    const gap = otherValue !== 0 ? (val - otherValue) / otherValue : 0;
    const plus = gap > 0 ? '+' : '';
    let cls = null;
    if (rowType === 'accelerated' || rowType === 'onTrack' || rowType === 'recent' || rowType === 'working') {
      cls = val >= otherValue ? 'label-success' : 'label-danger';
    } else {
      cls = val >= otherValue ? 'label-danger' : 'label-success';
    }

    const tip = this.getStatsTooltip(
      stats,
      rowType,
      camelCase(`owner tooltip ${rowType} ${stage.value}`),
      rowLabel,
      stage,
      isTotal,
    );

    const now = val * 100;

    return [
      (
        <td
          key={camelCase('-1')}
          className={`${styles.ownerCell} ${styles.withBorder}`}
        >
          {rowLabel}
        </td>
      ),
      (
        <td
          className={`${styles.ownerCell} text-right`}
          key={camelCase('-2')}
          width="50%"
        >
          <OverlayTrigger trigger={['hover']} placement="top" overlay={tip}>
            <a
              href=""
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.onSelect(stats.ids);
              }}
            >
              {reportType === 'amount' && <MoneyDisplay value={stackVal} precision={1} abbreviated />}
              {reportType === 'count' && <NumberDisplay value={stackVal} precision={0} suffix="deal" suffixIsUnit />}
            </a>
          </OverlayTrigger>
        </td>
      ),
      (
        <td
          className={`${styles.ownerCell} text-right`}
          key={camelCase('-3')}
        >
          <OverlayTrigger trigger={['hover']} placement="top" overlay={tip}>
            <a
              href=""
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.onSelect(stats.ids);
              }}
            >
              <NumberDisplay value={now} precision={1} suffix="%" />
            </a>
          </OverlayTrigger>
        </td>
      ),
      (
        <td
          className={`${styles.ownerCell} text-right`}
          key={camelCase('-4')}
        >
          <span className={`label ${cls}`}>{plus}<NumberDisplay value={gap * 100} precision={1} suffix="%" /></span>
        </td>
      ),
    ];
  }

  renderStageBar(stats, rowType, stage, segmentLabel, isTotal) {
    const { reportType, containerWidth } = this.state;
    const val = stats ? stats[camelCase(`${reportType} percentage`)] : 0;
    const now = val * 100;
    const label = numeral(val).format('0.0%');
    const tip = this.getStatsTooltip(
      stats,
      rowType,
      camelCase(`popover ${rowType} ${stage.value}`),
      `${segmentLabel} Pipeline`,
      stage,
      isTotal,
    );
    return (
      <OverlayTrigger trigger={['hover']} placement="top" overlay={tip}>
        {this.renderBar(now, val * containerWidth >= minBarWidth ? label : null, stats ? stats.ids : null, rowType)}
      </OverlayTrigger>
    );
  }

  renderStageStack(stats) {
    const { reportType } = this.state;
    const stackValue = stats ? stats[reportType] : 0;

    if (reportType === 'amount') {
      return (
        <MoneyDisplay value={stackValue} precision={1} abbreviated />
      );
    }
    return (
      <NumberDisplay value={stackValue} precision={0} suffix="deal" suffixIsUnit />
    );
  }

  renderGraph() {
    const { owners, velocityStages, velocityCategories } = this.props;
    const { ownerId, reportType } = this.state;
    const owner = ownerId ? find(owners, { user_id: ownerId }) : null;
    const hasOwner = owner !== null && owner !== undefined;
    const allStages = this.props.stages;

    const rows = reduce(allStages, (stages, stage) => {
      stages.push({
        stage, rowType: 'recent', label: 'Recent', isFirstRow: true,
      });
      stages.push({ stage, rowType: 'working', label: 'Working' });
      stages.push({ stage, rowType: 'stalled', label: 'Stalled' });
      return stages;
    }, []);

    const earlyStages = filter(rows, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'early');
    const earlyStageIndex = findIndex(rows, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'early');
    if (earlyStageIndex >= 0) {
      rows.splice(earlyStageIndex, 0, { rowType: 'early', label: 'Early Stages', total: earlyStages.length });
    }
    const lateStages = filter(rows, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'late');
    const lateStageIndex = findIndex(rows, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'late');
    if (lateStageIndex >= 0) {
      rows.splice(lateStageIndex, 0, { rowType: 'late', label: 'Late Stages', total: lateStages.length });
    }

    const totalRows = [
      {
        stage: { label: 'Pipeline Velocity', value: 'pipeline' }, rowType: 'accelerated', label: 'Accelerated', isTotal: true, isFirstRow: true,
      },
      {
        stage: { label: 'Pipeline Velocity', value: 'pipeline' }, rowType: 'onTrack', label: 'On-track', isTotal: true,
      },
      {
        stage: { label: 'Pipeline Velocity', value: 'pipeline' }, rowType: 'lagging', label: 'Lagging', isTotal: true,
      },
    ];

    const allRows = concat(totalRows, rows);

    allRows.splice(0, 0, { rowType: 'total', total: 3, label: '' });

    return (
      <div>
        <table className={`table table-condensed ${chartStyles.mainTable}`}>
          <thead>
            <tr>
              <th
                style={{ width: 10 }}
              >
                &nbsp;
              </th>
              <th
                colSpan="4"
                style={{ width: hasOwner ? '50%' : null }}
                className="text-center"
              >
                <span className={styles.mainHeader}>
                  Sales &amp; Stage Velocities<br />
                  <small>
                    <em>for {numeral(owners.length).format('0')} {pluralize('sales representation', owners.length)}</em>
                  </small>
                </span>
              </th>
              {hasOwner &&
                <th
                  style={{ width: '50%', position: 'relative' }}
                  colSpan="4"
                  className="text-center"
                >
                  <span className={styles.vs}>
                    vs
                  </span>
                  <span className={styles.mainHeader}>
                    Sales &amp; Stage Velocities<br />
                    <small>
                      <em>for {owner.name}</em>
                    </small>
                  </span>
                </th>
              }
            </tr>
          </thead>
          <tbody>
            {hasOwner &&
              <tr>
                <td colSpan="5">&nbsp;</td>
                <td className={`${styles.withBorder} ${styles.ownerCell}`}>&nbsp;</td>
                <td className={`text-right ${styles.ownerCell}`}><strong>{(reportType === 'amount' ? 'Amount' : 'Count')}</strong></td>
                <td className={`text-right ${styles.ownerCell}`}><strong>Actual</strong></td>
                <td className={`text-right ${styles.ownerCell}`}><strong>Gap</strong></td>
              </tr>
            }
            {allRows.map((editedStage) => {
              if (editedStage.rowType === 'early' || editedStage.rowType === 'late' || editedStage.rowType === 'total') {
                return [
                  (
                    <tr
                      key={`${editedStage.rowType}-0`}
                      className={`text-left ${chartStyles.divider} ${chartStyles.separateRow}`}
                    >
                      <td colSpan="3">&nbsp;</td>
                      <td width={hasOwner ? '49%' : '99%'}>
                        {editedStage.rowType === 'early' &&
                          <div ref={(el) => { this.container = el; }}>
                            <WindowResizeListener onResize={this.onWindowResize} />
                          </div>
                        }
                      </td>
                      <td colSpan={hasOwner ? 5 : 1} />
                    </tr>
                  ),
                  (
                    <tr
                      key={`${editedStage.rowType}-1`}
                      className={`${chartStyles.divider} ${chartStyles.separateRow}`}
                    >
                      <td rowSpan={editedStage.total + 1} className={chartStyles.groupColumn}>
                        <span className={chartStyles.rotatedStage}>
                          <span className="label text-uppercase">
                            {editedStage.label}
                          </span>
                        </span>
                      </td>
                    </tr>
                  ),
                ];
              }

              const { stage, isFirstRow, isTotal } = editedStage;
              let stats = null;
              let ownerStats = null;
              if (isTotal) {
                stats = velocityCategories[editedStage.rowType];
                ownerStats = hasOwner ? owner.categories[editedStage.rowType] : null;
              } else {
                if (velocityStages[stage.value] && velocityStages[stage.value].categories) {
                  stats = velocityStages[stage.value].categories[editedStage.rowType];
                }
                if (hasOwner && owner.stages && owner.stages[stage.value] && owner.stages[stage.value].categories) {
                  ownerStats = owner.stages[stage.value].categories[editedStage.rowType];
                }
              }
              const isSkipped = stage && stage.skip_conversion;
              const cls = [];
              if (isSkipped) {
                cls.push(chartStyles.skippedRow);
              }
              if (isFirstRow) {
                cls.push(chartStyles.divider);
              }
              if (isTotal) {
                cls.push(chartStyles.totalRow);
              }
              const skipTip = stage ? (
                <Tooltip
                  id={camelCase(`skipped ${editedStage.value}`)}
                  className={chartStyles.tooltip}
                >
                  <strong>{stage.label}</strong> stage is excluded from all calculations.
                </Tooltip>
              ) : null;

              return (
                <tr key={`${editedStage.rowType}-${stage.value}`} className={cls.join(' ')}>
                  {isFirstRow && !isSkipped &&
                    <td
                      rowSpan="3"
                      style={{ verticalAlign: 'middle' }}
                    >
                      <span className={chartStyles.stageName}>{stage.label}</span>
                    </td>
                  }
                  {isFirstRow && isSkipped &&
                    <td
                      colSpan={hasOwner ? 8 : 4}
                    >
                      <OverlayTrigger trigger={['hover']} placement="top" overlay={skipTip}>
                        <span className={`${chartStyles.stageName} text-muted`}>{stage.label} <Fa name="exclamation-triangle" /></span>
                      </OverlayTrigger>
                    </td>
                  }
                  {!isSkipped &&
                    <td className="text-right">
                      <nobr>{editedStage.label}</nobr>
                    </td>
                  }
                  {!isSkipped &&
                    <td className="text-right">
                      {this.renderStageBar(stats, editedStage.rowType, stage, editedStage.label, isTotal)}
                    </td>
                  }
                  {!isSkipped &&
                    <td className="text-right">
                      {this.renderStageStack(stats)}
                    </td>
                  }
                  {hasOwner && !isSkipped && this.renderOwnerCells(ownerStats, stats, editedStage.rowType, stage, editedStage.label, isTotal)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  render() {
    const { loading, loaded } = this.props;
    return (
      <div className={styles.chart}>
        {(loading || !loaded) && <ProgressBar2 />}
        {!loading && loaded &&
          <div>
            {this.renderControls()}
            <Element name="pipeline-velocity-chart" />
            {this.renderGraph()}
          </div>
        }
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  const velocityGraph = getVelocityGraphPipeline(state);
  return {
    stages: getStages(state),
    velocityStages: velocityGraph.stages,
    velocityCategories: velocityGraph.categories,
    loaded: getVelocityGraphLoaded(state),
    loading: getVelocityGraphLoading(state),
    owners: values(getVelocityGraphOwners(state)),
  };
}

VelocityGraph.propTypes = {
  loaded: PropTypes.bool,
  loading: PropTypes.bool,
  onSelect: PropTypes.func,
  velocityStages: PropTypes.shape({}),
  velocityCategories: PropTypes.shape({}),
  owners: PropTypes.arrayOf(PropTypes.shape({})),
  stages: PropTypes.arrayOf(PropTypes.shape({})),
};

VelocityGraph.defaultProps = {
  loaded: false,
  loading: false,
  velocityStages: {},
  velocityCategories: {},
  onSelect: () => {},
  owners: [],
  stages: [],
};
VelocityGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.shape({}).isRequired,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(VelocityGraph);
